import { useMemo } from 'react';
import { IconButton } from '@carbon/react';
import { Edit, TrashCan } from '@carbon/react/icons';
import { useAtomValue, useSetAtom } from 'jotai';
import classNames from 'classnames';

import { useDatasetConfig, useDatasetAtoms } from '../dataset-context';
import type { DatasetSearchParams } from '../types';
import { FilterChip } from '../filter-chip';
import {
  buildFilterGroups,
  disableGroupPayload,
  enableGroupPayload,
  type FilterChipData,
  type FilterGroup,
} from './selected-filters-utils';
import styles from './selected-filters-display.module.scss';

export const SelectedFiltersDisplay = () => {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const searchResult = useAtomValue(atoms.searchAtom);
  const setSearch = useSetAtom(atoms.searchAtom);
  const disabledFilters = useAtomValue(atoms.disabledFiltersAtom);
  const setDisabledFilters = useSetAtom(atoms.disabledFiltersAtom);
  const setFilterModalOpen = useSetAtom(atoms.filterModalOpenAtom);
  const setFilterCategory = useSetAtom(atoms.filterModalCategoryAtom);
  const payload = searchResult.currentPayload;
  const facets = useAtomValue(atoms.committedFacetsAtom) ?? undefined;

  // Enabled groups come from the live search payload; disabled ones from the
  // retained stash. A category is wholly in one or the other (disable is
  // per-group), so merge by category, preferring the enabled version.
  const groups = useMemo(() => {
    const enabled = payload
      ? buildFilterGroups(payload, config.filters, facets)
      : [];
    const disabled = buildFilterGroups(
      disabledFilters as DatasetSearchParams,
      config.filters,
      facets
    ).map((g) => ({ ...g, disabled: true }));

    const byCategory = new Map<string, FilterGroup>();
    enabled.forEach((g) => byCategory.set(g.category, g));
    disabled.forEach((g) => {
      if (!byCategory.has(g.category)) byCategory.set(g.category, g);
    });

    const order: string[] = [];
    const seen = new Set<string>();
    for (const f of config.filters) {
      if (!seen.has(f.category)) {
        seen.add(f.category);
        order.push(f.category);
      }
    }
    return order
      .filter((c) => byCategory.has(c))
      .map((c) => byCategory.get(c) as FilterGroup);
  }, [payload, disabledFilters, config.filters, facets]);

  if (groups.length === 0) return null;

  const removeChipFrom = (
    base: Partial<DatasetSearchParams>,
    chip: FilterChipData
  ): Partial<DatasetSearchParams> => {
    if (chip.arrayValue !== undefined) {
      const arr = (base[chip.paramKey] as string[] | undefined) ?? [];
      // An emptied array still counts as an active filter, so drop it entirely.
      const next = arr.filter((v) => v !== chip.arrayValue);
      return {
        ...base,
        [chip.paramKey]: next.length > 0 ? next : undefined,
      };
    }
    const cleared: Record<string, undefined> = { [chip.paramKey]: undefined };
    chip.extraParamKeys?.forEach((key) => {
      cleared[key] = undefined;
    });
    return { ...base, ...cleared };
  };

  const removeFilter = (chip: FilterChipData, disabled: boolean) => {
    if (disabled) {
      setDisabledFilters((current) => removeChipFrom(current, chip));
    } else {
      setSearch(
        (current) =>
          ({
            ...removeChipFrom(current ?? config.defaultPayload, chip),
            page: 1,
          }) as DatasetSearchParams
      );
    }
  };

  const clearGroup = (group: FilterGroup) => {
    const cleared: Record<string, undefined> = {};
    group.clearKeys.forEach((k) => {
      cleared[k] = undefined;
    });
    if (group.disabled) {
      setDisabledFilters((current) => ({ ...current, ...cleared }));
    } else {
      setSearch(
        (current) =>
          ({
            ...(current ?? config.defaultPayload),
            page: 1,
            ...cleared,
          }) as DatasetSearchParams
      );
    }
  };

  const editGroup = (category: string) => {
    setFilterCategory(category);
    setFilterModalOpen(true);
  };

  // Move a group's values out of the live payload into the disabled stash so
  // they stop affecting results but can be restored.
  const disableGroup = (group: FilterGroup) => {
    const next = disableGroupPayload(
      group.clearKeys,
      payload ?? config.defaultPayload,
      disabledFilters
    );
    setDisabledFilters(next.disabled);
    setSearch(() => next.payload);
  };

  const enableGroup = (group: FilterGroup) => {
    const next = enableGroupPayload(
      group.clearKeys,
      payload ?? config.defaultPayload,
      disabledFilters
    );
    setDisabledFilters(next.disabled);
    setSearch(() => next.payload);
  };

  return (
    <div className={styles.filterGroups}>
      {groups.map((group) => (
        <div key={group.category} className={styles.filterGroup}>
          <div className={styles.groupHeader}>
            <span
              className={classNames(styles.groupName, {
                [styles.groupNameDisabled]: group.disabled,
              })}
            >
              {group.category}
            </span>
            <button
              type="button"
              onClick={() => {
                if (group.disabled) enableGroup(group);
                else disableGroup(group);
              }}
              className={styles.groupToggle}
            >
              {group.disabled ? 'Enable' : 'Disable'}
            </button>
            <IconButton
              kind="ghost"
              size="sm"
              align="left"
              autoAlign
              label="Edit"
              onClick={() => editGroup(group.category)}
            >
              <Edit />
            </IconButton>
            <IconButton
              kind="ghost"
              size="sm"
              align="left"
              autoAlign
              label="Clear"
              onClick={() => clearGroup(group)}
            >
              <TrashCan />
            </IconButton>
          </div>
          <div className={styles.chipList}>
            {group.chips.map((chip) => (
              <FilterChip
                key={chip.key}
                disabled={group.disabled}
                onClose={() => removeFilter(chip, Boolean(group.disabled))}
              >
                {chip.label}
              </FilterChip>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
